import i18n from '@/i18n';
import type { Category, FilterState, Place } from '@/types';
import { getCategoryColor, getCategoryLabel, getPrimaryColor } from '@/utils/colors';
import { debounce, filterPlaces, getAllCategories, getCategoryCounts } from '@/utils/helpers';

export class UIManager {
  private places: Place[] = [];
  private filteredPlaces: Place[] = [];
  private filterState: FilterState = {
    searchQuery: '',
    selectedCategories: new Set(),
  };
  private onFilterChange?: (places: Place[]) => void;
  private onPlaceClick?: (place: Place) => void;
  private activeItemId: string | null = null;

  private searchInput: HTMLInputElement | null = null;
  private placesList: HTMLElement | null = null;
  private categoryFilters: HTMLElement | null = null;
  private sidebarTitle: HTMLElement | null = null;

  constructor() {
    this.initializeElements();
    this.setupEventListeners();
  }

  /**
   * Initialize DOM elements
   */
  private initializeElements(): void {
    this.searchInput = document.getElementById('search-input') as HTMLInputElement;
    this.placesList = document.getElementById('places-list');
    this.categoryFilters = document.getElementById('category-filters');
    this.sidebarTitle = document.querySelector('.sidebar-header h2');

    if (this.sidebarTitle) {
      this.sidebarTitle.textContent = i18n.t('sidebar.title');
    }

    if (this.searchInput) {
      this.searchInput.placeholder = i18n.t('sidebar.searchPlaceholder');
      this.searchInput.setAttribute('aria-label', i18n.t('sidebar.searchPlaceholder'));
    }
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    if (this.searchInput) {
      const debouncedSearch = debounce(() => {
        this.applyFilters();
      }, 300);

      this.searchInput.addEventListener('input', (e) => {
        this.filterState.searchQuery = (e.target as HTMLInputElement).value;
        debouncedSearch();
      });
    }
  }

  /**
   * Set places data and render UI
   */
  setPlaces(places: Place[]): void {
    this.places = places;
    this.filteredPlaces = [...places];
    this.renderCategoryFilters();
    this.renderPlacesList();
  }

  /**
   * Set filter change callback
   */
  onFilterChangeCallback(callback: (places: Place[]) => void): void {
    this.onFilterChange = callback;
  }

  /**
   * Set place click callback
   */
  onPlaceClickCallback(callback: (place: Place) => void): void {
    this.onPlaceClick = callback;
  }

  /**
   * Render category filters
   */
  private renderCategoryFilters(): void {
    if (!this.categoryFilters) return;

    const categoryCounts = getCategoryCounts(this.places);
    const categories = getAllCategories();

    const filtersHTML = categories
      .map((category) => {
        const count = categoryCounts.get(category) || 0;
        if (count === 0) return '';

        const color = getCategoryColor(category);
        const label = getCategoryLabel(category);
        const checked = this.filterState.selectedCategories.has(category) ? 'checked' : '';

        return `
          <div class="filter-item">
            <label>
              <input 
                type="checkbox" 
                data-category="${category}" 
                ${checked}
              />
              <span class="category-indicator" style="background-color: ${color};"></span>
              <span>${label}</span>
            </label>
            <span class="count">${count}</span>
          </div>
        `;
      })
      .join('');

    this.categoryFilters.innerHTML = `
      <h3>${i18n.t('filters.title')}</h3>
      <div class="filter-group">
        ${filtersHTML}
      </div>
    `;

    // Add event listeners to checkboxes
    this.categoryFilters.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const category = Number(target.dataset.category) as Category;

        if (target.checked) {
          this.filterState.selectedCategories.add(category);
        } else {
          this.filterState.selectedCategories.delete(category);
        }

        this.applyFilters();
      });
    });
  }

  /**
   * Render places list
   */
  private renderPlacesList(): void {
    if (!this.placesList) return;

    if (this.filteredPlaces.length === 0) {
      this.placesList.innerHTML = `
        <div class="empty-state">
          <p>${i18n.t('list.empty')}</p>
        </div>
      `;
      return;
    }

    const placesHTML = this.filteredPlaces
      .map((place) => {
        const badges = place.categories
          .slice(0, 2)
          .map(
            (cat) =>
              `<span class="badge" style="background-color: ${getCategoryColor(cat)};"></span>`
          )
          .join('');

        const categoryTags = place.categories
          .map((cat) => `<span class="category-tag">${getCategoryLabel(cat)}</span>`)
          .join('');

        const logoColor = getPrimaryColor(place.categories);
        const initialLetter = place.title.charAt(0);
        const logoHTML = place.brandLogo
          ? `<img src="${place.brandLogo}" alt="${place.title}" class="place-logo" />`
          : `<div class="place-logo placeholder" style="background-color: ${logoColor};"><span>${initialLetter}</span></div>`;

        const activeClass = this.activeItemId === place.id ? 'active' : '';

        return `
          <div class="place-item ${activeClass}" data-place-id="${place.id}">
            <div class="place-top-row">
              ${logoHTML}
              <div class="place-header">
                <h4>${place.title}</h4>
                <div class="category-badges">
                  ${badges}
                </div>
                <p class="place-description" title="${place.description}">${place.description}</p>
              </div>
            </div>         
            <div class="place-categories">
              ${categoryTags}
            </div>
          </div>
        `;
      })
      .join('');

    this.placesList.innerHTML = placesHTML;

    // Add click event listeners to place items
    this.placesList.querySelectorAll('.place-item').forEach((item) => {
      item.addEventListener('click', () => {
        const placeId = (item as HTMLElement).dataset.placeId;
        if (!placeId) return;

        const place = this.filteredPlaces.find((p) => p.id === placeId);

        if (place && this.onPlaceClick) {
          this.setActiveItem(placeId);
          this.onPlaceClick(place);
        }
      });
    });
  }

  /**
   * Apply filters and update UI
   */
  private applyFilters(): void {
    this.filteredPlaces = filterPlaces(this.places, this.filterState);
    this.renderPlacesList();

    if (this.onFilterChange) {
      this.onFilterChange(this.filteredPlaces);
    }
  }

  /**
   * Set active item in the list
   */
  setActiveItem(placeId: string): void {
    this.activeItemId = placeId;

    if (this.placesList) {
      this.placesList.querySelectorAll('.place-item').forEach((item) => {
        item.classList.remove('active');
        const itemPlaceId = (item as HTMLElement).dataset.placeId;
        if (itemPlaceId === placeId) {
          item.classList.add('active');
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }
  }

  /**
   * Show loading state
   */
  showLoading(): void {
    if (this.placesList) {
      this.placesList.innerHTML = `<div class="loading">${i18n.t('sidebar.loading')}</div>`;
    }
  }

  /**
   * Show error state
   */
  showError(message: string): void {
    if (this.placesList) {
      this.placesList.innerHTML = `
        <div class="error">
          <h3>${i18n.t('error.title')}</h3>
          <p>${message}</p>
        </div>
      `;
    }
  }

  /**
   * Get filtered places
   */
  getFilteredPlaces(): Place[] {
    return this.filteredPlaces;
  }
}
